import React, { useEffect, useLayoutEffect, useState } from 'react';
import '../styles/setVideo.css';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { SlLike, SlDislike } from 'react-icons/sl';
import { BsEye } from 'react-icons/bs';
import { BiShare } from 'react-icons/bi';
import { FiBookmark } from 'react-icons/fi';
import { FaRegCommentDots } from 'react-icons/fa';
import * as normalActions from '../store/actions';
import * as authAction from '../store/authAction';
import * as likeActions from '../data/like_data';
import * as actions from '../data/follow_data';
import Video from '../comps/video'
import CommentThread from '../comps/commentThread';
import CommentComposer from '../comps/commentComposer';
import { useMiniPlayer } from '../store/miniPlayerContext';
import { useAuthGate } from '../comps/authPrompt';
import UserAvatar from '../comps/userAvatar';
import PlaylistSave from '../comps/playlistSave';
import SubscribeControl from '../comps/subscribeControl';
import LongType from '../comps/LongType';
import { isPhotoPost, isShortPost, mediaPath, shareMedia } from '../utils/mediaRoute';
import { firstPostImage, parsePostImages } from '../utils/postImages';
import { commentFromSearch, scrollToComment } from '../utils/commentFocus';

const SetVideo = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [showComments, setShowComments] = useState(() => Boolean(commentFromSearch(location.search)));
  const commentTarget = React.useRef(commentFromSearch(location.search));
  const [wideScreen, setWideScreen] = useState(() => typeof window !== 'undefined' && window.matchMedia('(min-width: 1000px)').matches);
  const [replyTo, setReplyTo] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState('');
  const [reportHint, setReportHint] = useState('');
  const [shareHint, setShareHint] = useState('');
  const { setMiniVideo, setMiniFromRect, playbackRef, expanding } = useMiniPlayer();

  const post = useSelector(state => state.PostReducer.post || {});
  const Comments = useSelector(state => state.CommentsReduse.Comments.filter(comm => String(comm.postid) === String(id)));
  const like = useSelector(state => state.UpdsReduse.upds.filter(item => item.postid === id && item.type === 'p'));
  const dislike = useSelector(state => state.UpdsReduse.upds.filter(item => item.postid === id && item.type === 'd'));
  const eyeView = useSelector(state => state.UpdsReduse.view || 0);
  const { requireAuth, promptEl } = useAuthGate();
  const userid = useSelector(state => state.authReduse.id);
  const pid = useSelector(state => state.authReduse.pid);
  const userPhoto = useSelector(state => state.authReduse.photo);
  const userEmail = useSelector(state => state.authReduse.email);
  const userCountry = useSelector(state => state.authReduse.postion);
  const likeL = useSelector(state => state.UpdsReduse.upds.find(item => item.userid === userid && item.postid === id && item.type === 'p'));
  const dislikeL = useSelector(state => state.UpdsReduse.upds.find(item => item.userid === userid && item.postid === id && item.type === 'd'));
  const translations = useSelector(state => state.lang_reduse.lang || {});
  const recommendations = useSelector(state => state.PostReducer.lav || [])
    .filter(item => String(item.id) !== String(id) && !isPhotoPost(item))
    .slice(0, 20);

  const currentPost = String(post.id) === String(id)
    ? post
    : (location.state && location.state.item && String(location.state.item.id) === String(id) ? location.state.item : {});
  const playerWrapRef = React.useRef(null);
  const startStamp = React.useRef({ id: null, time: 0, play: true });
  if (startStamp.current.id !== id) {
    const same = playbackRef && playbackRef.current && playbackRef.current.id === id;
    startStamp.current = {
      id,
      time: same ? (playbackRef.current.time || 0) : 0,
      play: same ? playbackRef.current.playing !== false : true
    };
  }

  const reportTime = (info) => {
    if (!playbackRef) return;
    playbackRef.current = {
      id,
      time: info.time || 0,
      playing: !!info.playing
    };
  };

  const captureMiniFrom = () => {
    const el = playerWrapRef.current;
    if (!el || !setMiniFromRect) return;
    const rect = el.getBoundingClientRect();
    if (rect.width > 40 && rect.height > 40) {
      setMiniFromRect({ left: rect.left, top: rect.top, width: rect.width, height: rect.height });
    }
  };

  const goBackToMini = () => {
    captureMiniFrom();
    if (window.history.state && typeof window.history.state.idx === 'number' && window.history.state.idx > 0) {
      navigate(-1);
      return;
    }
    navigate('/');
  };

  useLayoutEffect(() => {
    if (expanding) return undefined;
    captureMiniFrom();
    return () => captureMiniFrom();
  });

  useEffect(() => {
    if (!currentPost.id) return;
    if (isPhotoPost(currentPost)) {
      navigate(`/post/${encodeURIComponent(currentPost.id)}`, { replace: true, state: { item: currentPost, source: 'watch', feed: [currentPost] } });
      return;
    }
    if (isShortPost(currentPost)) {
      navigate(mediaPath(currentPost), { replace: true, state: { item: currentPost } });
      return;
    }
    setMiniVideo({
      id: currentPost.id,
      title: currentPost.title,
      image: currentPost.image,
      type: currentPost.type,
      email: currentPost.email || currentPost.user || currentPost.name || ''
    });
  }, [currentPost.id, currentPost.title, currentPost.image, currentPost.type, currentPost.email, currentPost.user, currentPost.name, setMiniVideo, navigate]);

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1000px)');
    const onChange = () => setWideScreen(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const loadVideo = async () => {
      setIsLoading(true);
      const storage = localStorage.getItem('userdata');
      if (storage) {
        try {
          const { id: authId, pid } = JSON.parse(storage);
          await dispatch(authAction.auth(authId, pid));
          await dispatch(normalActions.add_to_views(authId, id));
        } catch (err) {
          console.warn('Auth failed', err);
        }
      }
      try {
        await dispatch(likeActions.get_post(id));
        if (localStorage.getItem('userdata')) await dispatch(actions.get_follow());
      } catch (err) {
        console.warn('Load video failed', err);
      }
      setIsLoading(false);
    };
    loadVideo();
  }, [dispatch, id]);

  useEffect(() => {
    if (!currentPost.id || isPhotoPost(currentPost)) return;
    dispatch(likeActions.get_recommendations(currentPost.id)).catch(err => {
      console.warn('Recommendations failed', err);
    });
  }, [dispatch, currentPost.id, currentPost.type]);

  const handleUpvote = () => {
    if (!requireAuth()) return;
    dispatch(normalActions.add_to_upds('p', userid, id));
  };
  const handleDownvote = () => {
    if (!requireAuth()) return;
    dispatch(normalActions.add_to_upds('d', userid, id));
  };

  const repliesByParent = Comments.reduce((map, comment) => {
    const parentKey = comment.parentId ? String(comment.parentId) : '';
    if (!parentKey) return map;
    map[parentKey] = map[parentKey] || [];
    map[parentKey].push(comment);
    return map;
  }, {});
  const rootComments = Comments.filter(comment => !comment.parentId);

  const handleAddComment = async ({ text, media }) => {
    if (!requireAuth()) return;
    if ((!text && !media) || !userid || !pid) return;
    await dispatch(normalActions.add_to_comments(text, userid, id, userPhoto, userEmail, pid, replyTo?.id || null, media));
    setReplyTo(null);
  };

  const handleDeleteComment = comment => {
    if (!requireAuth()) return;
    if (String(comment.userid) !== String(userid)) return;
    dispatch(normalActions.delete_comment(comment.id, userid, pid));
  };

  const handleShare = async event => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    setShareHint('');
    const result = await shareMedia(currentPost.id ? currentPost : { id, title: currentPost.title, type: currentPost.type || 'l', image: currentPost.image });
    if (result === 'abort') return;
    setShareHint(result === 'copied' ? 'Link copied' : 'Shared');
    window.setTimeout(() => setShareHint(''), 2200);
  };

  const openReport = event => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (!requireAuth()) return;
    setReportHint('');
    setReportOpen(true);
  };

  const submitReport = async event => {
    event.preventDefault();
    if (!requireAuth() || !userid) return;
    const content = reportText.trim();
    if (content.length < 2) {
      setReportHint('Tell us why you are reporting this.');
      return;
    }
    try {
      await dispatch(normalActions.add_to_report(id, userid, content, userCountry || ''));
      setReportHint('Report sent');
      setReportText('');
      window.setTimeout(() => {
        setReportOpen(false);
        setReportHint('');
      }, 900);
    } catch (err) {
      setReportHint(err.message || 'Could not send report');
    }
  };

  useEffect(() => {
    const wanted = commentTarget.current;
    if (!wanted) return undefined;
    setShowComments(true);
    if (!Comments.length) return undefined;
    let tries = 0;
    const tick = () => {
      if (scrollToComment(wanted) || tries > 24) return;
      tries += 1;
      window.setTimeout(tick, 150);
    };
    tick();
    return undefined;
  }, [Comments.length, id]);

  return (
    <section className="SetVideoPage">
      <Helmet>
        <title>{currentPost.title || 'Video'}</title>
        <meta name="description" content={currentPost.description || 'Watch this video'} />
        <meta name="keywords" content={`video,pinweelo website,${currentPost.tages || ''}`} />
        <link rel="canonical" href={`https://pinweelo.com/video/${id}`} />
      </Helmet>

      {promptEl}

      <div className="SetVideoPage__container">
        <div className="SetVideoPage__videoPanel">
            <div className="SetVideoPage__playerSlot">
              <div className="SetVideoPage__playerWrapper" ref={playerWrapRef}>
                <Video
                    title={currentPost.title}
                    data={currentPost.image ? `/api/uploads/${currentPost.image}` : ''}
                    busy={!currentPost.image}
                    onBack={goBackToMini}
                    share={<button type="button" className="videoShareButton" onClick={handleShare}><BiShare /></button>}
                    onReport={openReport}
                    captionText={[currentPost.title, currentPost.description].filter(Boolean).join('. ')}
                    onEnded={() => {
                      const next = recommendations[0];
                      if (!next) return;
                      navigate(mediaPath(next), { state: { item: next } });
                    }}
                    startAt={startStamp.current.time}
                    autoPlay={startStamp.current.play}
                    muted={!!expanding}
                    poster={currentPost.thumbnail ? `/api/uploads/${currentPost.thumbnail}` : undefined}
                    onProgress={reportTime}
                  />
              </div>
          </div>

          <div className="SetVideoPage__scroll">
          <div className="SetVideoPage__videoDetails">
            <h1 className="SetVideoPage__videoTitle">{currentPost.title}</h1>
            <div className="SetVideoPage__titleRow">
              <div className="SetVideoPage__channelProfile" onClick={() => navigate(`/@${currentPost.userid}`)}>
                <UserAvatar src={currentPost.userImg} name={currentPost.email} alt={currentPost.email} userId={currentPost.userid} />
                <div>
                  <strong>{currentPost.email}</strong>
                  <p>{Number(currentPost.followers) || 0} {Number(currentPost.followers) === 1 ? 'subscriber' : 'subscribers'}</p>
                </div>
              </div>
              <SubscribeControl leaderId={currentPost.userid} email={currentPost.email} />
            </div>

            <div className="SetVideoPage__actionRow">
              <button className={likeL ? 'active' : ''} onClick={handleUpvote}>
                <SlLike /> {like.length} Like
              </button>
              <button className={dislikeL ? 'active' : ''} onClick={handleDownvote}>
                <SlDislike /> {dislike.length} Dislike
              </button>
              <button type="button" className={`SetVideoPage__commentsBtn${showComments ? ' active' : ''}`} onClick={() => setShowComments(prev => !prev)} aria-label="Comments">
                <FaRegCommentDots /> {Comments.length}
              </button>
              <button type="button" className="SetVideoPage__shareBtn" onClick={handleShare}>
                <BiShare /> Share
              </button>
              <PlaylistSave saveOnly icon={<><FiBookmark /> Save</>} post={currentPost} />
            </div>

            <div className="SetVideoPage__bottomRow">
              <span>{eyeView} views</span>
              <span>•</span>
              <span>{currentPost.date}</span>
            </div>

            <div className="SetVideoPage__descriptionBlock">
              <p>{currentPost.description}</p>
            </div>

            {(showComments || wideScreen) && (
              <div className="SetVideoPage__comments">
                <h3>Comments</h3>
                {rootComments.length === 0 && <p className="commentsEmpty">Be the first to comment</p>}
                <div className="commentList">
                  {rootComments.map((comment, index) => (
                    <CommentThread
                      key={comment.id}
                      comment={comment}
                      repliesByParent={repliesByParent}
                      onReply={comment => requireAuth() && setReplyTo(comment)}
                      onVote={(type, commentId) => requireAuth() && dispatch(normalActions.add_to_comupds(type, userid, commentId))}
                      onDelete={handleDeleteComment}
                      canDelete={item => String(item.userid) === String(userid)}
                      isLast={index === rootComments.length - 1}
                    />
                  ))}
                </div>
                <CommentComposer
                  replyTo={replyTo}
                  onCancelReply={() => setReplyTo(null)}
                  userPhoto={userPhoto}
                  userEmail={userEmail}
                  onFocusAuth={() => requireAuth()}
                  onSubmit={handleAddComment}
                />
              </div>
            )}
          </div>

        <div className="SetVideoPage__recommendSection">
          <div className="SetVideoPage__recommendList">
            {recommendations.map(item => (
              <div
                key={item.id || item.title}
                className="SetVideoPage__recommendItem"
                onClick={() => navigate(mediaPath(item), { state: { item } })}
              >
                <LongType
                  postId={item.id}
                  userid={item.userid}
                  photo={isPhotoPost(item)}
                  photoCount={parsePostImages(item.image).length}
                  recommend
                  setVideo
                  thumbnail={item.thumbnail || firstPostImage(item.image)}
                  title={item.title}
                  tages={item.tages}
                  date={item.date}
                  views={item.views}
                  vlikes={item.vlikes}
                  vdislikes={item.vdislikes}
                  vcomments={item.vcomments}
                  email={item.email}
                  userImg={item.userImg}
                />
              </div>
            ))}
          </div>
        </div>
          </div>
        </div>
      </div>
      {reportOpen ? (
        <div className="videoReportModal" onClick={() => setReportOpen(false)}>
          <form className="videoReportCard" onClick={event => event.stopPropagation()} onSubmit={submitReport}>
            <h2>Report this video</h2>
            <textarea
              value={reportText}
              onChange={event => setReportText(event.target.value)}
              placeholder="Why are you reporting this?"
              maxLength={500}
              rows={4}
            />
            {reportHint ? <p>{reportHint}</p> : null}
            <div>
              <button type="button" onClick={() => setReportOpen(false)}>Cancel</button>
              <button type="submit">Send report</button>
            </div>
          </form>
        </div>
      ) : null}
    </section>
  );
};

export default SetVideo;
